import React, { useMemo } from "react";

import { type QueryView, Type } from "~/models/dto";
import { createSlug } from "~/tools/create-slug";
import { isEmpty } from "~/tools/is-empty";
import { useHandler } from "~/tools/use-handler";

import { usePersistNodeFlow } from "../../../queries";
import { VIEW_PATH } from "../../../routes/Routes";
import { useNavigateToLinkLazy } from "../../react/link";
import { notification } from "../../react/notification/Notification";
import { buildElement } from "../../react/setting-node/SettingNodeUtils";
import { InlineLink } from "../../react/styled/StyledComponents";

import { viewValidationSchema } from "./_view-form-utils";

type ViewFormValue = Pick<QueryView, "name" | "description" | "identifier">;

export const isEditView = (view: QueryView): boolean => view.id != null;

interface UseViewOpsResult {
	isLoading: boolean;
	initialValue?: ViewFormValue;
	handleSubmit: (value: ViewFormValue) => Promise<void>;
}

export const useViewOps = ({ view, closeDrawer }: { view: QueryView; closeDrawer: VoidFunction }): UseViewOpsResult => {
	const navigateToLink = useNavigateToLinkLazy();

	const { mutate: saveAsViewNewNew, isLoading } = usePersistNodeFlow(Type.QueryView, "view", (savedView) => {
		notification.success({
			message:
				view.id == null ? (
					<>
						The view has been successfully saved as{" "}
						<InlineLink
							data-testid="view-modal-success-toast-view-name"
							href={
								// it can use Router.Link here because the notification is out of Router context
								"#" +
								navigateToLink(VIEW_PATH, {
									params: {
										slug: savedView.identifier ?? createSlug(savedView.name),
									},
									search: ["timeRange", "timestamp", "detachedFilters"],
								})
							}
						>
							{savedView.name}
						</InlineLink>
					</>
				) : (
					`The view "${savedView.name}" has been successfully saved.`
				),
		});
		closeDrawer();
	});

	const initialValue = useMemo<ViewFormValue>(
		() => ({
			name: view.name,
			description: view.description ?? "",
			identifier: view.identifier,
		}),
		[view],
	);

	const handleSubmit = useHandler(async (data: ViewFormValue) => {
		const result = buildElement<QueryView>({
			schema: viewValidationSchema,
			values: {
				...data,
				identifier: isEmpty(data.identifier) ? undefined : data.identifier,
				viewType: undefined,
			},
			id: view!.id,
			type: Type.QueryView,
		});

		await saveAsViewNewNew("save", { ...view, ...result });
	});

	return {
		isLoading,
		initialValue,
		handleSubmit,
	};
};
